import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import useInternalLinkNavigation from "./hooks/useInternalLinkNavigation.js";
import useRouteScrollBehaviour from "./hooks/useRouteScrollBehaviour.js";
import useRouteLanguage from "./hooks/useRouteLanguage.js";
import useLanguageQueryRedirect from "./hooks/useLanguageQueryRedirect.js";
import { hasLocalizedPage } from "./content/pages/registry.js";
import { stripLanguagePrefix } from "./lib/language.js";
import Home from "./pages/Home";
import WeddingPhotoSharing from "./pages/WeddingPhotoSharing";
import DigitalWeddingInvitation from "./pages/DigitalWeddingInvitation";
import WeddingPlanning from "./pages/WeddingPlanning";
import WeddingRsvp from "./pages/WeddingRsvp";
import WeddingSeating from "./pages/WeddingSeating";
import Guide from "./pages/Guide";
import GuidesIndex from "./pages/GuidesIndex";
import Contact from "./pages/Contact";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiePolicy from "./pages/CookiePolicy";

/**
 * Every page this site routes, stated once.
 *
 * Rendered twice: at the root, where it serves English, and inside the `/el`
 * subtree below, where the same components serve Greek. React Router matches a
 * descendant `<Routes>` against the pathname left over after the parent's
 * match, so these paths need no `/el` variants — `/el/wedding-planning` reaches
 * `path="/wedding-planning"` with the prefix already consumed.
 *
 * That is deliberately not a second route table. A Greek page is the same page
 * at a different URL, and a duplicated table is a table that will disagree with
 * itself the first time a route is added to one half of it.
 */
const pageRoutes = () => [
  <Route key="home" path="/" element={<Home />} />,
  <Route key="home-alias" path="/home" element={<Home />} />,
  <Route
    key="photo-sharing"
    path="/wedding-photo-sharing"
    element={<WeddingPhotoSharing />}
  />,
  <Route
    key="invitation"
    path="/digital-wedding-invitation"
    element={<DigitalWeddingInvitation />}
  />,
  <Route key="planning" path="/wedding-planning" element={<WeddingPlanning />} />,
  <Route key="rsvp" path="/wedding-rsvp" element={<WeddingRsvp />} />,
  <Route key="seating" path="/wedding-seating" element={<WeddingSeating />} />,
  /* The guides index — the footer's Guides destination, and linked
     from nowhere else. It is noindex and absent from the sitemap; see
     the note in the page. Declared before the :slug route so the static
     segment wins. */
  <Route key="guides" path="/guides" element={<GuidesIndex />} />,
  /* One route for every informational guide — the slug is resolved
     against src/content/guides/registry.js, and an unregistered one is
     sent home. Adding a guide never touches this file. */
  <Route key="guide" path="/guides/:slug" element={<Guide />} />,
  <Route key="contact" path="/contact" element={<Contact />} />,
  <Route key="terms" path="/terms" element={<TermsOfService />} />,
  <Route key="privacy" path="/privacy-policy" element={<PrivacyPolicy />} />,
  <Route key="cookies" path="/cookie-policy" element={<CookiePolicy />} />,
  <Route key="catchall" path="*" element={<Home />} />,
];

/**
 * The language subtree, and the gate in front of it.
 *
 * A `/el/…` URL is only rendered when the page registry says that page exists
 * in Greek. Anything else is sent to its English counterpart, preserving the
 * query string and fragment, so a stale or guessed Greek URL lands on the real
 * page rather than on a Greek-flavoured homepage.
 *
 * Every page on the site is registered in Greek today — the homepage, the five
 * product pages, Contact, the three legal pages, the guides index and all
 * fourteen guides — and each is a real static document rather than an English
 * one relabelled client-side. Registering a page in Greek turns its redirect
 * into a render, here, with no change to this file, which is why this gate has
 * survived the site going from one Greek URL to forty-nine.
 *
 * The gate is not dead code now that nothing is English-only. It is what still
 * catches `/el/guides/<english-slug>` — a real address a reader can type or a
 * stale link can carry, whose page exists only under its Greek slug — and sends
 * it to the English guide rather than rendering a Greek-flavoured 404.
 *
 * The gate lives in the router rather than in `vercel.json` on purpose. Vercel
 * evaluates redirects before rewrites regardless of their order in the file, so
 * a blanket `/el/(.*)` redirect would pre-empt every Greek rewrite ever added
 * and there would be nothing in the config that looked wrong.
 */
const LocalizedRoutes = ({ language }) => {
  const { pathname, search, hash } = useLocation();
  const basePath = stripLanguagePrefix(pathname);

  if (!hasLocalizedPage(basePath, language)) {
    return <Navigate to={`${basePath}${search}${hash}`} replace />;
  }
  return <Routes>{pageRoutes()}</Routes>;
};

function App() {
  // Every internal `<a href>` on the site becomes a client-side navigation from
  // this one handler — see the hook for why a delegate rather than a link
  // component. Anything it does not own (new-tab gestures, external hosts,
  // non-routed paths, links whose own handler already ran) falls through to the
  // browser untouched.
  const handleInternalLinkClick = useInternalLinkNavigation();
  useRouteScrollBehaviour();

  // The URL is the language, on every navigation and not only on the first
  // paint: `/el/…` renders Greek, and an unprefixed page that HAS a Greek URL
  // renders English. Mounted here rather than inside the `/el` subtree because
  // leaving that subtree is the direction that used to be missed.
  useRouteLanguage();

  // `?lang=el` is how `app/` and `invite/` have always linked here. Once the
  // requested language has a real page, that link is moved onto its own URL
  // rather than rendering a second version of an English one — see the hook.
  const languageRedirect = useLanguageQueryRedirect();
  if (languageRedirect) return <Navigate to={languageRedirect} replace />;

  return (
    <div className="app-wrapper" onClick={handleInternalLinkClick}>
      <Routes>
        {/* The Greek subtree, declared before the English routes so `/el/…`
            reaches it rather than the catch-all. */}
        <Route path="/el/*" element={<LocalizedRoutes language="el" />} />
        {pageRoutes()}
      </Routes>
    </div>
  );
}

export default App;
